import React from 'react';
import { Skull } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useCampaign } from '../../context/CampaignContext';
import { Button } from '../common/Button';
import { hasSubclass, hasSubclassFeature } from '../../utils/featureDetection';

const MAX_FEAR = 12;

const GMFearCounter = ({ characters = [] }) => {
  const { sharedGameState, updateSharedGameState } = useCampaign();
  const gmFear = sharedGameState?.gmFear?.current || 0;

  const adjustFear = async (delta) => {
    const newFear = Math.max(0, Math.min(MAX_FEAR, gmFear + delta));
    try {
      await updateSharedGameState('gmFear', {
        current: newFear,
        lastUpdated: Date.now()
      });
    } catch (error) {
      console.error('Failed to update GM Fear:', error);
      toast.error('Failed to update Fear');
    }
  };

  // Detect party abilities that can spend/remove GM Fear
  const fearAbilities = [];

  characters.forEach(char => {
    // Ethereal Visage (Winged Sentinel Seraph)
    // SRD: "When you succeed with Hope on a Presence Roll, you can remove a Fear
    // from the GM's Fear pool instead of gaining Hope."
    if (hasSubclass(char, 'winged-sentinel')) {
      fearAbilities.push({
        id: `ethereal-${char.id}`,
        label: `Ethereal Visage (${char.name?.split(' ')[0]})`,
        description: 'Remove 1 Fear (Hope success on Presence)'
      });
    }

    // Apex Predator (Ranger specialization)
    // SRD: "Before you make an attack roll against your Focus, you can spend a Hope.
    // On a successful attack, you remove a Fear from the GM's Fear pool."
    if (hasSubclassFeature(char, 'Apex Predator')) {
      fearAbilities.push({
        id: `apex-${char.id}`,
        label: `Apex Predator (${char.name?.split(' ')[0]})`,
        description: 'Remove 1 Fear (successful Focus attack)'
      });
    }
  });

  const handleAbilitySpend = async (ability) => {
    if (gmFear <= 0) return;
    await adjustFear(-1);
    toast.success(`${ability.label} removed 1 Fear!`, { icon: '🎯', duration: 3000 });
  };

  return (
    <div className="bg-gradient-to-br from-gray-900 to-black p-4 rounded-lg shadow-2xl border border-red-700 flex flex-col">
      <h3 className="text-lg font-bold text-red-400 font-serif mb-4 flex items-center gap-2">
        <Skull className="w-5 h-5 text-red-400" />
        GM Fear Pool
      </h3>

      {/* Counter */}
      <div className="flex items-center justify-center gap-4 mb-4">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => adjustFear(-1)}
          disabled={gmFear === 0}
          ariaLabel="Decrease fear"
        >
          -1
        </Button>
        <div className="text-center">
          <div className="text-4xl font-bold text-red-400 min-w-[60px] tabular-nums">
            {gmFear}
          </div>
          <div className="text-xs text-gray-500">/ {MAX_FEAR}</div>
        </div>
        <Button
          variant="dangerOutline"
          size="sm"
          onClick={() => adjustFear(1)}
          disabled={gmFear >= MAX_FEAR}
          ariaLabel="Increase fear"
        >
          +1
        </Button>
      </div>

      {/* Contextual ability buttons */}
      {fearAbilities.length > 0 && (
        <div className="space-y-2 border-t border-gray-700 pt-3">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Party Abilities</p>
          {fearAbilities.map(ability => (
            <button
              key={ability.id}
              onClick={() => handleAbilitySpend(ability)}
              disabled={gmFear === 0}
              className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                gmFear > 0
                  ? 'bg-red-900/30 hover:bg-red-900/50 text-red-300 border border-red-800/50'
                  : 'bg-gray-800/30 text-gray-600 border border-gray-700/50 cursor-not-allowed'
              }`}
              title={ability.description}
            >
              <span className="font-medium">{ability.label}</span>
              <span className="block text-xs text-gray-500 mt-0.5">{ability.description}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default GMFearCounter;
